import {Api} from '@common/constants';
import {get, post, put, _delete} from '@core/services/httpMethods';
import BASE_URL from '@core/helpers/constants';
import store from '../../../rootStore/store';
import {selectWorkflowId} from '../store/workflowSelectors';
import {
  WorkflowApiResponse,
  WorkflowDTO,
  CreateWorkflowRequest,
  InputComponentDTO,
  UpdateInputComponentRequest,
  RulesetComponentDTO,
  AddRulesetComponentRequest,
  UpdateRulesetComponentRequest,
} from '../types/workflow';

// Base URL for workflow APIs
const getWorkflowBaseUrl = () => `${BASE_URL}${Api.RULE_ENGINE_BASE_URL}`;

// Helper function to get current workflow ID from store
const getCurrentWorkflowId = (): string => {
  const state = store.getState();
  const workflowId = selectWorkflowId(state);
  if (!workflowId) {
    throw new Error(
      'No current workflow found in store. Please fetch workflow first.'
    );
  }
  return workflowId;
};

// Workflow APIs
export const createWorkflow = async (
  data: CreateWorkflowRequest
): Promise<WorkflowApiResponse<WorkflowDTO>> => {
  const response = await post(
    `${getWorkflowBaseUrl()}${Api.WORKFLOWS_CREATE}`,
    data
  );
  return response as WorkflowApiResponse<WorkflowDTO>;
};

export const getWorkflow = async (
  workflowId: string
): Promise<WorkflowApiResponse<WorkflowDTO>> => {
  const response = await get(
    `${getWorkflowBaseUrl()}${Api.WORKFLOWS}/${workflowId}`
  );
  return response as WorkflowApiResponse<WorkflowDTO>;
};

// Input Component APIs
export const fetchInputComponent = async (
  inputComponentId: string
): Promise<WorkflowApiResponse<InputComponentDTO>> => {
  const workflowId = getCurrentWorkflowId();
  const response = await get(
    `${getWorkflowBaseUrl()}/workflows/${workflowId}/input/${inputComponentId}`
  );
  return response as WorkflowApiResponse<InputComponentDTO>;
};

export const updateInputComponent = async (
  inputComponentId: string,
  data: UpdateInputComponentRequest
): Promise<WorkflowApiResponse<InputComponentDTO>> => {
  const workflowId = getCurrentWorkflowId();
  const response = await put(
    `${getWorkflowBaseUrl()}/workflows/${workflowId}/input/${inputComponentId}`,
    data
  );
  return response as WorkflowApiResponse<InputComponentDTO>;
};

// Ruleset Component APIs
export const addRulesetComponent = async (
  data: AddRulesetComponentRequest
): Promise<WorkflowApiResponse<RulesetComponentDTO>> => {
  const workflowId = getCurrentWorkflowId();
  const response = await post(
    `${getWorkflowBaseUrl()}/workflows/${workflowId}/ruleset`,
    data
  );
  return response as WorkflowApiResponse<RulesetComponentDTO>;
};

export const updateRulesetComponent = async (
  rulesetId: string,
  data: UpdateRulesetComponentRequest
): Promise<WorkflowApiResponse<RulesetComponentDTO>> => {
  const workflowId = getCurrentWorkflowId();
  const response = await put(
    `${getWorkflowBaseUrl()}/workflows/${workflowId}/ruleset/${rulesetId}`,
    data
  );
  return response as WorkflowApiResponse<RulesetComponentDTO>;
};

export const deleteRulesetComponent = async (
  rulesetId: string
): Promise<WorkflowApiResponse<void>> => {
  const workflowId = getCurrentWorkflowId();
  const response = await _delete(
    `${getWorkflowBaseUrl()}/workflows/${workflowId}/ruleset/${rulesetId}`
  );
  return response as WorkflowApiResponse<void>;
};

export const fetchRulesetComponent = async (
  rulesetId: string
): Promise<WorkflowApiResponse<RulesetComponentDTO>> => {
  const workflowId = getCurrentWorkflowId();
  const response = await get(
    `${getWorkflowBaseUrl()}/workflows/${workflowId}/ruleset/${rulesetId}`
  );
  return response as WorkflowApiResponse<RulesetComponentDTO>;
};

// Helper function to get tenant header (configurable)
export const getTenantHeaders = (tenantId?: string) => {
  const headers: Record<string, string> = {};
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }
  return headers;
};
